import { onMounted, onUnmounted, ref } from 'vue';

import type { Ref } from 'vue';

const CHECK_INTERVAL_MS = 5 * 60_000;
const IDLE_RELOAD_AFTER_MS = 10 * 60_000;
const IDLE_POLL_MS = 60_000;

/**
 * Keeps the eternally-open PWA window on the latest frontend release.
 *
 * Polls `/version.json` (emitted at build time, served with `no-cache` by
 * Caddy) every 5 minutes and on tab visibility. On mismatch shows a sticky
 * toast with a manual reload button; additionally performs a silent reload
 * when the secretary is idle ≥ 10 min, has no unsent text in any input and
 * the pending-chats queue is empty (so the live badge source is never killed
 * at a hot moment).
 */
export const useVersionCheck = (pendingCount: Ref<number>) => {
  const updateAvailable = ref(false);

  const toast = useToast();

  let notified = false;
  let lastActivityAt = Date.now();
  let checkTimer: number | null = null;
  let idleTimer: number | null = null;

  const onActivity = () => {
    lastActivityAt = Date.now();
  };

  const hasDirtyInput = (): boolean => {
    const fields = document.querySelectorAll<HTMLInputElement | HTMLTextAreaElement>(
      'textarea, input:not([type]), input[type="text"], input[type="search"], input[type="email"]',
    );

    for (const field of fields) {
      if (field.value.trim().length > 0) {
        return true;
      }
    }

    const editables = document.querySelectorAll<HTMLElement>('[contenteditable="true"]');

    for (const editable of editables) {
      if ((editable.textContent ?? '').trim().length > 0) {
        return true;
      }
    }

    return false;
  };

  const check = async () => {
    try {
      const response = await fetch('/version.json', { cache: 'no-store' });

      if (!response.ok) {
        return;
      }

      const data = await response.json() as { version?: string };

      if (!data.version || data.version === __APP_VERSION__) {
        return;
      }

      updateAvailable.value = true;

      if (!notified) {
        notified = true;
        toast.add({
          title: 'Доступне оновлення',
          description: 'Вийшла нова версія застосунку.',
          icon: 'i-lucide-refresh-cw',
          color: 'info',
          duration: 0,
          actions: [
            {
              label: 'Оновити',
              color: 'info',
              onClick: () => {
                window.location.reload();
              },
            },
          ],
        });
      }
    } catch {
      // Network hiccup — the next poll will retry.
    }
  };

  const maybeAutoReload = () => {
    if (!updateAvailable.value) {
      return;
    }

    if (Date.now() - lastActivityAt < IDLE_RELOAD_AFTER_MS) {
      return;
    }

    if (pendingCount.value > 0 || hasDirtyInput()) {
      return;
    }

    window.location.reload();
  };

  const onVisibilityChange = () => {
    if (document.visibilityState === 'visible') {
      void check();
    }
  };

  onMounted(() => {
    if (__APP_VERSION__ === 'dev') {
      return;
    }

    window.addEventListener('pointerdown', onActivity, { passive: true });
    window.addEventListener('keydown', onActivity, { passive: true });
    document.addEventListener('visibilitychange', onVisibilityChange);

    checkTimer = window.setInterval(() => void check(), CHECK_INTERVAL_MS);
    idleTimer = window.setInterval(maybeAutoReload, IDLE_POLL_MS);

    void check();
  });

  onUnmounted(() => {
    window.removeEventListener('pointerdown', onActivity);
    window.removeEventListener('keydown', onActivity);
    document.removeEventListener('visibilitychange', onVisibilityChange);

    if (checkTimer !== null) {
      window.clearInterval(checkTimer);
    }

    if (idleTimer !== null) {
      window.clearInterval(idleTimer);
    }
  });

  return { updateAvailable };
};
